import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, output, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { IIroNamRequest, IroNamRequestStatus } from '@shs-digi-care-monorepo/interfaces';
import {
    TableComponent,
    TableHeadComponent,
    TableHeaderComponent,
    TableRowComponent,
    TableCellComponent,
    RequestsStatusPillComponent,
    MobileRowFieldComponent,
    MobileTableCardComponent,
    MobileTableComponent,
    SortOrder,
    statusPillColorMap
} from '@shs-digi-care-monorepo/frontend-lib';
import { TableDateCellComponent } from '../table-date-cell/table-date-cell.component';
import { TabFilterState } from '../../state/tab-filter-state';

@Component({
    selector: 'app-created-requests-table',
    templateUrl: './created-requests-table.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        TableComponent,
        TableHeadComponent,
        TableHeaderComponent,
        TableRowComponent,
        TableCellComponent,
        RequestsStatusPillComponent,
        MobileRowFieldComponent,
        MobileTableCardComponent,
        MobileTableComponent,
        TableDateCellComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CreatedRequestsTableComponent {
    public tasks = input.required<IIroNamRequest[]>();
    public loading = input.required<boolean>();
    public total = input.required<number>();
    public activeState = input.required<TabFilterState>();

    private router = inject(Router);

    public sortChange = output<{ column: string; order: SortOrder | null }>();
    public deleteRequests = output<IIroNamRequest[]>();

    protected statusPillColorMap = statusPillColorMap;

    protected readonly selectedIds = signal<Set<string>>(new Set());
    public readonly selectionMode = signal(false);

    public readonly hasSavedRequests = computed(() => this.tasks().some((r) => r.status === IroNamRequestStatus.SAVED));

    public readonly allSavedSelected = computed(() => {
        const saved = this.tasks().filter((r) => r.status === IroNamRequestStatus.SAVED);
        return saved.length > 0 && saved.every((r) => this.selectedIds().has(r.id));
    });

    public readonly selectedCount = computed(() => this.selectedIds().size);

    public enterSelectionMode(): void {
        this.selectionMode.set(true);
        this.selectedIds.set(new Set());
    }

    public cancelSelectionMode(): void {
        this.selectionMode.set(false);
        this.selectedIds.set(new Set());
    }

    public confirmDelete(): void {
        const ids = this.selectedIds();
        const selected = this.tasks().filter((r) => ids.has(r.id));
        if (selected.length > 0) {
            this.deleteRequests.emit(selected);
        }
    }

    public clearSelection(): void {
        this.selectedIds.set(new Set());
        this.selectionMode.set(false);
    }

    public toggleAll(checked: boolean): void {
        const ids = new Set(this.selectedIds());
        for (const request of this.tasks()) {
            if (request.status === IroNamRequestStatus.SAVED) {
                if (checked) {
                    ids.add(request.id);
                } else {
                    ids.delete(request.id);
                }
            }
        }
        this.selectedIds.set(ids);
    }

    protected toggleRow(request: IIroNamRequest, checked: boolean): void {
        const ids = new Set(this.selectedIds());
        if (checked) {
            ids.add(request.id);
        } else {
            ids.delete(request.id);
        }
        this.selectedIds.set(ids);
    }

    protected openRequest(request: IIroNamRequest): void {
        if (this.selectionMode()) {
            return;
        }
        if (request.status === IroNamRequestStatus.SAVED) {
            this.router.navigate(['/request'], { queryParams: { requestId: request.id } });
        } else {
            this.router.navigate(['request', request.id]);
        }
    }
}
